"use client"

import { Card, CardContent, CardFooter } from "@/components/ui/card"
import { FaTag } from "react-icons/fa6"
import Link from "next/link"
import { usePathname } from "next/navigation"
import OptimizedImage from "@/components/ui/Image/OptimizedImage"
import { generateSlug } from "@/utils/helpers/utils"

type CalculatorCardProps = {
   title: string
   description: string
   imageSrc: string
   href: string
   categoryName?: string
}

export default function CalculatorCard({ title, description, imageSrc, href, categoryName }: CalculatorCardProps) {
   const pathname = usePathname()
   const segments = pathname?.split("/") ?? []
   const currentResourceType = segments[2] || "calculators"

   const activeCategory = categoryName ?? "Calculators"
   const categoryLink = `/resources/${currentResourceType}/${generateSlug(activeCategory)}`

   return (
      <Card className="group col-span-full flex h-full flex-col overflow-hidden rounded-2xl border border-gray-100 bg-white p-0 shadow-sm transition hover:shadow-lg sm:col-span-1">
         <Link href={href}>
            <div className="relative flex h-64 w-full items-center justify-center overflow-hidden bg-gray-100">
               <OptimizedImage
                  src={imageSrc}
                  alt={title}
                  width={400}
                  height={300}
                  className="h-full w-full object-cover object-center"
               />

               <Link
                  aria-label={`View more items in ${activeCategory}`}
                  href={categoryLink}
                  className="absolute right-4 top-4 flex items-center gap-2 rounded-md border border-gray-100 bg-[#FAF8F7] px-3 py-2 shadow-sm transition-colors hover:bg-custom-primary/20"
               >
                  <FaTag className="h-4 w-4 text-[#EF5929]" aria-hidden="true" />
                  <span className="text-custom-xs-medium uppercase text-[#EF5929]">{activeCategory}</span>
               </Link>
            </div>

            <CardContent className="flex-1 space-y-5 p-6">
               <h2 className="line-clamp-2 h-[4.5rem] font-lora text-custom-26-bold text-black">{title}</h2>

               {description && (
                  <p className="line-clamp-3 min-h-[4rem] text-custom-base-medium text-[#333333]">{description}</p>
               )}
            </CardContent>

            <CardFooter className="p-6 pt-2">
               <Link
                  href={href}
                  className="inline-flex items-center gap-2 rounded-lg bg-custom-primary p-4 text-base font-semibold text-white transition-transform"
               >
                  Use Calculator
               </Link>
            </CardFooter>
         </Link>
      </Card>
   )
}
